import React, { useState, useEffect, useRef } from 'react'
import { getPartnerDisplayName } from './partnerDisplayName'

const MIN_SEARCH_LENGTH = 2

/**
 * DoublesPartnerSearch – partner picker with server-side search.
 *
 * Partners are auto-added (no confirmation required). Any app user who
 * is not already in this league is eligible.
 *
 * Props:
 *   onSearch           – async (query) => { partners, total }
 *   loading            – bool, true while search is in flight
 *   nominatedPartner   – null | { type: 'player', id, label, full_name?, first_name?, last_name?, doubles_skill_level }
 *   onSelectPartner    – callback(partner | null)
 */
function DoublesPartnerSearch({
  onSearch,
  loading,
  searchError = '',
  nominatedPartner,
  onSelectPartner,
}) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [results, setResults] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchFailed, setSearchFailed] = useState(false)
  const inputRef = useRef(null)

  // Debounce: wait 300ms after last keystroke before searching server
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  // Trigger server search when debounced query changes
  useEffect(() => {
    const trimmedQuery = debouncedQuery.trim()

    if (!trimmedQuery || trimmedQuery.length < MIN_SEARCH_LENGTH) {
      setResults([])
      setTotalCount(0)
      setHasSearched(false)
      setSearchFailed(false)
      return
    }

    let cancelled = false
    const doSearch = async () => {
      try {
        setSearchFailed(false)
        const response = await onSearch(trimmedQuery)
        if (!cancelled) {
          setResults(Array.isArray(response?.partners) ? response.partners : [])
          setTotalCount(response?.total ?? 0)
          setHasSearched(true)
        }
      } catch {
        if (!cancelled) {
          setResults([])
          setTotalCount(0)
          setHasSearched(true)
          setSearchFailed(true)
        }
      }
    }
    doSearch()
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, onSearch])

  const selectedDisplayLabel = getPartnerDisplayName(nominatedPartner) || null

  const handleSelectPlayer = (partner) => {
    const fullName = getPartnerDisplayName(partner)

    onSelectPartner?.({
      type: 'player',
      id: partner.id,
      label: fullName,
      full_name: fullName,
      first_name: partner.first_name ?? '',
      last_name: partner.last_name ?? '',
      doubles_skill_level: partner.doubles_skill_level ?? null,
    })
    setQuery('')
    setDebouncedQuery('')
    setResults([])
    setHasSearched(false)
  }

  const handleClear = () => {
    onSelectPartner?.(null)
    setQuery('')
    setDebouncedQuery('')
    setResults([])
    setTotalCount(0)
    setHasSearched(false)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const normalizedQuery = query.trim()
  const normalizedDebouncedQuery = debouncedQuery.trim()
  const hasQuery = normalizedDebouncedQuery.length >= MIN_SEARCH_LENGTH
  const isInputSettled = normalizedQuery === normalizedDebouncedQuery
  const hasError = searchFailed || !!searchError
  const hasNoResults =
    hasQuery &&
    isInputSettled &&
    hasSearched &&
    results.length === 0 &&
    !loading &&
    !hasError
  const hasMoreResults = hasSearched && totalCount > results.length

  return (
    <div className="mt-4">
      <label
        htmlFor="doubles-partner-search"
        className="form-label fw-semibold"
      >
        Doubles Partner
      </label>

      {/* ── Selected state ── */}
      {nominatedPartner ? (
        <div
          className="border rounded p-3 bg-light"
          data-testid="partner-selected-card"
          role="status"
          aria-label={`Doubles partner: ${selectedDisplayLabel}`}
        >
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-semibold" data-testid="partner-selected-name">
              {selectedDisplayLabel}
            </span>
            {nominatedPartner.doubles_skill_level != null && (
              <span className="badge bg-secondary">
                {nominatedPartner.doubles_skill_level} NTRP
              </span>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-auto"
              onClick={handleClear}
              aria-label="Change or remove doubles partner"
            >
              Change
            </button>
          </div>
          <p className="mb-0 text-muted small mt-2">
            Your partner will be automatically added to the league when you
            complete registration.
          </p>
        </div>
      ) : (
        /* ── Search state ── */
        <div className="position-relative">
          <input
            ref={inputRef}
            id="doubles-partner-search"
            type="text"
            role="combobox"
            className="form-control"
            placeholder="Search by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search for doubles partner by name"
            aria-autocomplete="list"
            aria-controls="partner-search-listbox"
            aria-expanded={results.length > 0}
            aria-haspopup="listbox"
            autoComplete="off"
            data-testid="partner-search-input"
          />

          {/* Loading indicator */}
          {loading && hasQuery && (
            <div className="mt-2 text-muted small">
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Searching…
            </div>
          )}

          {/* Results dropdown */}
          {results.length > 0 && (
            <ul
              id="partner-search-listbox"
              role="listbox"
              aria-label="Partner search results"
              className="list-unstyled border rounded shadow-sm position-absolute w-100 bg-white mb-0"
              style={{
                zIndex: 100,
                top: '100%',
                maxHeight: '240px',
                overflowY: 'auto',
              }}
            >
              {results.map((partner) => (
                <li key={partner.id} role="option" aria-selected="false">
                  <button
                    type="button"
                    className="btn btn-link w-100 text-start px-3 py-2 text-decoration-none text-body d-flex justify-content-between align-items-center"
                    onClick={() => handleSelectPlayer(partner)}
                    data-testid={`partner-option-${partner.id}`}
                  >
                    <span>{getPartnerDisplayName(partner)}</span>
                    {partner.doubles_skill_level != null && (
                      <span className="text-muted small">
                        {partner.doubles_skill_level} NTRP
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Search error */}
          {hasError && hasQuery && !loading && (
            <div
              className="mt-2 alert alert-warning py-2 px-3"
              role="alert"
              data-testid="search-error-section"
            >
              <span className="small">
                {searchError || 'Search failed. Please try again.'}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-warning ms-2"
                onClick={() => {
                  setSearchFailed(false)
                  setDebouncedQuery('')
                  setTimeout(() => setDebouncedQuery(query.trim()), 10)
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* No results */}
          {hasNoResults && (
            <div className="mt-2" data-testid="no-results-section">
              <p className="text-muted small mb-0">
                No eligible players found matching &ldquo;{debouncedQuery}
                &rdquo;.
              </p>
            </div>
          )}

          {/* More results hint */}
          {hasMoreResults && (
            <p
              className="text-muted small mt-1 mb-0"
              data-testid="refine-search-hint"
            >
              Showing {results.length} of {totalCount} matches &mdash; type more
              to narrow results.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default DoublesPartnerSearch
